import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  IconButton,
  Drawer,
  Button,
  Divider,
  Tooltip,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import HistoryIcon from '@mui/icons-material/History'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonPinIcon from '@mui/icons-material/PersonPin'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import PatientMonitoringDialog from 'src/supportPortal/components/PatientMonitoringDialog'
import { canManageMonitoring } from 'src/supportPortal/services/patientMonitoringService'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { formatPhoneNumber } from 'src/consumer/helpers/utilFunctions'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import useInfiniteScroll from 'react-infinite-scroll-hook'

dayjs.extend(utc)

// Tab configuration - uses eventType filter values from the API
// API: GET /api/secure/cc/support/patient/search
// eventType values: consultation, therapy-session, screening, assessment, etc.
const PATIENT_TABS = [
  { label: 'All Patients', value: 'all', eventType: null },
  { label: 'Consultation Patients', value: 'consultation', eventType: 'consultation' },
  { label: 'Therapy Patients', value: 'therapy', eventType: 'therapy-session' },
  { label: 'Screened Patients', value: 'screened', eventType: 'screening' },
]

// Event type options for visit history filter
const EVENT_TYPE_OPTIONS = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'therapy-session', label: 'Therapy Session' },
  { value: 'screening', label: 'Screening' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'nutrition-consultation', label: 'Nutrition' },
  { value: '1on1', label: '1-on-1' },
  { value: 'interview', label: 'Interview' },
  { value: 'webinar', label: 'Webinar' },
]

// Styled components
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  minHeight: 48,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}))

const PatientCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: `1px solid ${theme.palette.grey[200]}`,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 4px 12px rgba(103, 121, 227, 0.15)',
    transform: 'translateY(-2px)',
  },
}))

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 520,
    maxWidth: '95vw',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
    [theme.breakpoints.down('md')]: {
      width: '100vw',
    },
  },
}))

export default function SupportPractitionerPatientsPage() {
  const { mentorSlug } = useParams()
  const location = useLocation()
  
  // Determine if this is doctors or therapists page based on URL
  const isDoctors = location.pathname.includes('/doctors/')
  const practitionerType = isDoctors ? 'Doctor' : 'Therapist'
  
  // Get mentor data from session storage
  const mentorData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('supportMentorData') || '{}')
    } catch {
      return {}
    }
  }, [])
  
  const practitionerId = mentorData?.id
  const practitionerName = mentorData?.name || mentorData?.fullName || practitionerType
  
  // State
  const [activeTab, setActiveTab] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [patients, setPatients] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [visitHistory, setVisitHistory] = useState([])
  const [visitHistoryLoading, setVisitHistoryLoading] = useState(false)
  const [visitHistoryEventTypes, setVisitHistoryEventTypes] = useState([]) // Filter for visit history
  const [monitoringDialogOpen, setMonitoringDialogOpen] = useState(false)
  const [monitoringPatient, setMonitoringPatient] = useState(null)
  
  const searchTimeoutRef = useRef(null)
  const pageSize = 20
  
  // Check if user can manage monitoring
  const hasMonitoringPermission = useMemo(() => canManageMonitoring(), [])
  
  // Get current eventType filter value from selected tab
  const currentEventType = PATIENT_TABS[activeTab]?.eventType
  
  // Fetch patients using the patient search API with practitionerId filter
  // API: GET /api/secure/cc/support/patient/search
  const fetchPatients = useCallback(async (pageToFetch = 1, search = '', eventType = null, append = false) => {
    if (!practitionerId) {
      console.error('Missing practitionerId - required for patient search')
      setIsLoading(false)
      return
    }
    
    if (pageToFetch === 1) {
      setIsLoading(true)
    }
    
    try {
      const params = {
        practitionerId, // Filter by practitioner
        page: pageToFetch,
        pageSize,
      }
      
      // Add search query
      if (search) {
        params.search = search
      }
      
      // Add eventType filter if specified (for tab filtering)
      // API accepts: consultation, therapy-session, screening, assessment, etc.
      if (eventType) {
        params.eventType = eventType
      }
      
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/patient/search',
        params,
        undefined,
        undefined,
        true,
      )
      
      if (resp.status === 200) {
        // The API returns a simple array of PatientInfo objects
        const newPatients = Array.isArray(resp.data) 
          ? resp.data 
          : resp.data?.data || []
        
        if (append) {
          setPatients(prev => [...prev, ...newPatients])
        } else {
          setPatients(newPatients)
        }
        
        setHasMore(newPatients.length >= pageSize)
        setPage(pageToFetch)
      } else {
        // Fallback to empty array
        if (!append) {
          setPatients([])
        }
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error fetching patients:', error)
      if (!append) {
        setPatients([])
      }
      setHasMore(false)
    } finally {
      setIsLoading(false)
    }
  }, [practitionerId, pageSize])
  
  // Initial fetch and refetch on tab change
  useEffect(() => {
    setPatients([])
    setPage(1)
    setHasMore(true)
    fetchPatients(1, searchQuery, currentEventType, false)
  }, [activeTab, practitionerId])
  
  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setPatients([])
      setPage(1)
      setHasMore(true)
      fetchPatients(1, value, currentEventType, false)
    }, 300)
  }
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setSearchQuery('')
  }
  
  // Load more patients
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchPatients(page + 1, searchQuery, currentEventType, true)
    }
  }, [isLoading, hasMore, page, searchQuery, currentEventType, fetchPatients])
  
  // Infinite scroll hook
  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  })
  
  // Load visit history for selected patient with optional eventType filter
  const loadVisitHistory = useCallback(async (patientId, eventTypes = []) => {
    if (!patientId) return
    
    setVisitHistoryLoading(true)
    try {
      // Build query params - filter by practitioner
      let url = `/api/secure/cc/support/booked-event-history-of-a-patient?patientId=${patientId}&sortDescendingOnStartTime=true&limit=100`
      
      // Add practitionerId filter
      if (practitionerId) {
        url += `&practitionerId=${practitionerId}`
      }
      
      // Add eventType filters (repeat param for each type)
      if (eventTypes && eventTypes.length > 0) {
        eventTypes.forEach((et) => {
          url += `&eventType=${et}`
        })
      }
      
      const response = await getCPDataWithoutParams(url)
      
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        Array.isArray(response?.data?.data?.['booked-event-info'])
      ) {
        const bookedEvents = response?.data?.data?.['booked-event-info']
          ?.filter((item) => item?.status === 'booked')
          ?.sort((a, b) => {
            const timeA = dayjs.utc(a?.startTime).valueOf()
            const timeB = dayjs.utc(b?.startTime).valueOf()
            return timeB - timeA
          })
        setVisitHistory(bookedEvents || [])
      } else {
        setVisitHistory([])
      }
    } catch (error) {
      console.error('Error fetching visit history:', error)
      setVisitHistory([])
    } finally {
      setVisitHistoryLoading(false)
    }
  }, [practitionerId])
  
  // Handle patient click
  const handlePatientClick = (patient) => {
    setSelectedPatient(patient)
    setDrawerOpen(true)
    setVisitHistoryEventTypes([]) // Reset filter
    loadVisitHistory(patient?.id, [])
  }
  
  // Close drawer
  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedPatient(null)
    setVisitHistory([])
    setVisitHistoryEventTypes([])
  }

  // Handle visit history filter change
  const handleVisitHistoryFilterChange = (eventTypes) => {
    setVisitHistoryEventTypes(eventTypes)
    if (selectedPatient) {
      loadVisitHistory(selectedPatient.id, eventTypes)
    }
  }
  
  // Handle monitoring button click
  const handleMonitoringClick = (e, patient) => {
    if (e) e.stopPropagation()
    setMonitoringPatient(patient)
    setMonitoringDialogOpen(true)
  }

  // Handle monitoring update success
  const handleMonitoringSuccess = (updatedPatientInfo) => {
    if (updatedPatientInfo) {
      // Update the patient in the list
      setPatients((prev) =>
        prev.map((p) => (p.id === updatedPatientInfo.id ? { ...p, ...updatedPatientInfo } : p)),
      )
      // Also update the selected patient if drawer is open
      if (selectedPatient?.id === updatedPatientInfo.id) {
        setSelectedPatient(updatedPatientInfo)
      }
    }
  }
  
  // Utility functions
  const getPatientAge = (dateOfBirth) => {
    if (!dateOfBirth) return null
    return getAgeFromDobWithTime(dateOfBirth)
  }
  
  const getContactInfo = (patient) => {
    if (patient.email) {
      return { type: 'email', value: patient.email }
    }
    if (patient.mobile) {
      const phoneDetails = formatPhoneNumber(patient.mobile)
      const formatted = phoneDetails?.formattedPhoneNumber 
        ? `(${phoneDetails.countryCode}) ${phoneDetails.formattedPhoneNumber}`
        : patient.mobile
      return { type: 'mobile', value: formatted }
    }
    return { type: 'none', value: null }
  }
  
  const getAvatarColor = (gender) => {
    if (!gender) return 'rgba(103, 121, 227, 0.1)'
    const genderLower = gender.toLowerCase()
    if (genderLower === 'male') return 'rgba(59, 130, 246, 0.1)'
    if (genderLower === 'female') return 'rgba(236, 72, 153, 0.1)'
    return 'rgba(103, 121, 227, 0.1)'
  }
  
  const getIconColor = (gender) => {
    if (!gender) return '#6779e3'
    const genderLower = gender.toLowerCase()
    if (genderLower === 'male') return '#3b82f6'
    if (genderLower === 'female') return '#ec4899'
    return '#6779e3'
  }
  
  const getEventTypeColor = (eventType) => {
    const colors = {
      consultation: '#6779e3',
      'therapy-session': '#7450aa',
      therapySession: '#7450aa',
      therapy: '#7450aa',
      screening: '#f0a6aa',
      assessment: '#875e8a',
    }
    return colors[eventType] || '#6779e3'
  }
  
  // Render patient card
  const renderPatientCard = (patient) => {
    const age = getPatientAge(patient.dateOfBirth)
    const contact = getContactInfo(patient)
    
    return (
      <PatientCard key={patient.id} onClick={() => handlePatientClick(patient)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              backgroundColor: getAvatarColor(patient.gender),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: `2px solid ${getIconColor(patient.gender)}30`,
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 32, color: getIconColor(patient.gender) }} />
            {age && (
              <Typography
                sx={{
                  position: 'absolute',
                  fontSize: '10px',
                  fontWeight: 700,
                  color: 'white',
                  backgroundColor: getIconColor(patient.gender),
                  borderRadius: '6px',
                  padding: '2px 6px',
                  marginTop: '44px',
                }}
              >
                {age}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} color="text.primary" noWrap>
              {patient.name || 'Unknown Patient'}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {patient.patientNumber && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  #{patient.patientNumber}
                </Typography>
              )}
              {contact.type === 'mobile' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {contact.value}
                  </Typography>
                </Box>
              )}
              {contact.type === 'email' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {contact.value}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasMonitoringPermission && (
              <Tooltip title={patient.monitored ? 'Manage Monitoring' : 'Enable Monitoring'}>
                <Chip
                  icon={<MonitorHeartIcon sx={{ fontSize: '14px !important' }} />}
                  label={patient.monitored ? 'Monitored' : 'Monitor'}
                  size="small"
                  onClick={(e) => handleMonitoringClick(e, patient)}
                  sx={{
                    height: 24,
                    fontSize: '11px',
                    cursor: 'pointer',
                    backgroundColor: patient.monitored ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                    color: patient.monitored ? '#10b981' : '#64748b',
                    '&:hover': {
                      backgroundColor: patient.monitored ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                    },
                  }}
                />
              </Tooltip>
            )}
            <IconButton size="small" sx={{ color: 'primary.main' }}>
              <PersonIcon />
            </IconButton>
          </Box>
        </Box>
      </PatientCard>
    )
  }
  
  // Render patient details drawer
  const renderPatientDetailsDrawer = () => {
    if (!selectedPatient) return null
    
    const age = getPatientAge(selectedPatient.dateOfBirth)
    const contact = getContactInfo(selectedPatient)
    
    return (
      <StyledDrawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              borderBottom: '2px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5" fontWeight={600}>
                Patient Details
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {hasMonitoringPermission && (
                  <Tooltip title={selectedPatient?.monitored ? 'Manage Monitoring' : 'Enable Monitoring'}>
                    <Button
                      size="small"
                      startIcon={<MonitorHeartIcon />}
                      variant={selectedPatient?.monitored ? 'contained' : 'outlined'}
                      color={selectedPatient?.monitored ? 'success' : 'inherit'}
                      onClick={() => handleMonitoringClick(null, selectedPatient)}
                    >
                      {selectedPatient?.monitored ? 'Monitored' : 'Monitor'}
                    </Button>
                  </Tooltip>
                )}
                <IconButton onClick={handleCloseDrawer} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '16px',
                  backgroundColor: getAvatarColor(selectedPatient.gender),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `3px solid ${getIconColor(selectedPatient.gender)}40`,
                }}
              >
                <AccountCircleIcon sx={{ fontSize: 36, color: getIconColor(selectedPatient.gender) }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {selectedPatient.name || 'Unknown Patient'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {selectedPatient.patientNumber && (
                    <Chip
                      label={`#${selectedPatient.patientNumber}`}
                      size="small"
                      sx={{ fontFamily: 'monospace', fontSize: '11px' }}
                    />
                  )}
                  {age && (
                    <Chip
                      label={`${age} years`}
                      size="small"
                      sx={{ backgroundColor: getAvatarColor(selectedPatient.gender), color: getIconColor(selectedPatient.gender) }}
                    />
                  )}
                  {selectedPatient.gender && (
                    <Chip
                      label={selectedPatient.gender}
                      size="small"
                      sx={{ backgroundColor: getAvatarColor(selectedPatient.gender), color: getIconColor(selectedPatient.gender) }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {/* Contact Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                Contact Information
              </Typography>
              {contact.type === 'mobile' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2">{contact.value}</Typography>
                </Box>
              )}
              {contact.type === 'email' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2">{contact.value}</Typography>
                </Box>
              )}
              {contact.type === 'none' && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No contact information available
                </Typography>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Visit History */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HistoryIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Visit History with {practitionerName}
                </Typography>
              </Box>
              
              {/* Event Type Filter Toggles */}
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Filter by Event Type
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {EVENT_TYPE_OPTIONS.map((option) => {
                    const isSelected = visitHistoryEventTypes.includes(option.value)
                    const eventColor = getEventTypeColor(option.value)
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        size="small"
                        onClick={() => {
                          const newTypes = isSelected
                            ? visitHistoryEventTypes.filter((t) => t !== option.value)
                            : [...visitHistoryEventTypes, option.value]
                          handleVisitHistoryFilterChange(newTypes)
                        }}
                        sx={{
                          height: 26,
                          fontSize: '11px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          backgroundColor: isSelected ? `${eventColor}20` : 'white',
                          color: isSelected ? eventColor : '#64748b',
                          border: `1.5px solid ${isSelected ? eventColor : '#e2e8f0'}`,
                          '&:hover': {
                            backgroundColor: isSelected ? `${eventColor}30` : '#f1f5f9',
                            borderColor: eventColor,
                          },
                        }}
                      />
                    )
                  })}
                  {visitHistoryEventTypes.length > 0 && (
                    <Chip
                      label="Clear All"
                      size="small"
                      onClick={() => handleVisitHistoryFilterChange([])}
                      sx={{
                        height: 26,
                        fontSize: '11px',
                        cursor: 'pointer',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        border: '1.5px solid #ef4444',
                        '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.2)' },
                      }}
                    />
                  )}
                </Box>
              </Box>
              
              {visitHistoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : visitHistory.length > 0 ? (
                <Box sx={{ position: 'relative', pl: 3 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '11px',
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      backgroundColor: 'rgba(103, 121, 227, 0.2)',
                    }}
                  />
                  {visitHistory.map((visit, index) => {
                    const eventTypeColor = getEventTypeColor(visit.eventType)
                    const eventTypeName = displayNamesOfEventTypes[visit.eventType] || visit.eventType || 'Session'
                    const visitDate = dayjs.utc(visit.startTime).local()
                    
                    return (
                      <Box key={visit.id || index} sx={{ position: 'relative', mb: 2 }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '-20px',
                            top: '4px',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            backgroundColor: eventTypeColor,
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        />
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${eventTypeColor}40`,
                            backgroundColor: `${eventTypeColor}05`,
                          }}
                        >
                          <Chip
                            label={eventTypeName}
                            size="small"
                            sx={{
                              backgroundColor: `${eventTypeColor}15`,
                              color: eventTypeColor,
                              fontWeight: 600,
                              fontSize: '11px',
                              mb: 1,
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight={500}>
                              {visitDate.format('dddd, DD MMM YYYY')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {visitDate.format('h:mm A')}
                            </Typography>
                          </Box>
                          {visit.mentorName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <PersonPinIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                with {visit.mentorName}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    )
                  })}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 4,
                    borderRadius: 2,
                    backgroundColor: 'rgba(103, 121, 227, 0.02)',
                    border: '1px dashed rgba(103, 121, 227, 0.2)',
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No visit history found
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </StyledDrawer>
    )
  }
  
  return (
    <>
      <SupportHeader heading={`Patients - ${practitionerName}`} showBackButton />
      
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {/* Search Box */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search patients by name, mobile, or patient number..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('')
                      setPatients([])
                      setPage(1)
                      setHasMore(true)
                      fetchPatients(1, '', currentEventType, false)
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>
        
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: 3,
              },
            }}
          >
            {PATIENT_TABS.map((tab, index) => (
              <StyledTab key={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        
        {/* Content */}
        {isLoading && patients.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : patients.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <PersonIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Patients Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? `No patients match "${searchQuery}"`
                : `No ${PATIENT_TABS[activeTab]?.label.toLowerCase()} for this ${practitionerType.toLowerCase()}`}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing {patients.length} patient{patients.length !== 1 ? 's' : ''}
            </Typography>
            
            {patients.map(renderPatientCard)}
            
            {/* Infinite scroll sentinel */}
            {(hasMore || isLoading) && (
              <Box ref={sentryRef} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
        )}
      </Box>
      
      {/* Patient Details Drawer */}
      {renderPatientDetailsDrawer()}

      {/* Patient Monitoring Dialog */}
      <PatientMonitoringDialog
        open={monitoringDialogOpen}
        onClose={() => {
          setMonitoringDialogOpen(false)
          setMonitoringPatient(null)
        }}
        patient={monitoringPatient}
        onSuccess={handleMonitoringSuccess}
      />
    </>
  )
}
